import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { CalendarClock, ChevronRight, Gauge, ShieldCheck } from "@fleet/icons";
import { cn, type UpcomingItem } from "@tendly/shared";
import { useI18n } from "@/providers/i18n-provider";
import {
  assetKindIcon,
  DUE_STATUS_CLASSES,
  DUE_STATUS_EDGE,
  dueStatusLabels,
  usageUnitLabel,
} from "@/features/assets/asset-display";

interface UpcomingListProps {
  items: UpcomingItem[];
  /** Hides asset name (asset detail view). */
  hideAssetName?: boolean;
}

/** Upcoming dues list (reminders + warranties) by urgency. */
export function UpcomingList(props: UpcomingListProps) {
  const { t, locale } = useI18n();

  const dueLabel = (item: UpcomingItem): string => {
    if (item.status === "unknown") {
      return t(msg`Add a service or reading to project the due date`);
    }
    const parts: string[] = [];
    if (item.dueDate) {
      parts.push(item.dueDate.toLocaleDateString(locale()));
    }
    if (item.due?.dueBy === "usage" && item.due.usageUntilDue !== null) {
      const remaining = item.due.usageUntilDue;
      const unit = usageUnitLabel(t, item.usageUnit);
      parts.push(
        remaining >= 0
          ? `${remaining.toLocaleString(locale())} ${unit} ${t(msg`left`)}`
          : `${Math.abs(remaining).toLocaleString(locale())} ${unit} ${t(msg`over`)}`,
      );
    } else if (item.daysUntilDue !== null) {
      parts.push(
        item.daysUntilDue >= 0
          ? t(msg`{days} days left`, { days: item.daysUntilDue })
          : t(msg`{days} days overdue`, { days: Math.abs(item.daysUntilDue) }),
      );
    }
    return parts.join(" · ");
  };

  return (
    <ul class="flex flex-col gap-2" data-testid="upcoming-list">
      <For each={props.items}>
        {(item) => {
          const KindIcon = assetKindIcon(item.assetKind);
          return (
            <li>
              <Link
                to="/assets/$assetId"
                params={{ assetId: item.assetId }}
                class={cn(
                  "flex items-center gap-3 rounded-xl border border-l-[3px] border-border/40 bg-card/40 px-4 py-3 transition-[border-color,background-color,box-shadow,transform] duration-200 will-change-transform hover:border-border hover:bg-card/70 hover:shadow-sm active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40",
                  DUE_STATUS_EDGE[item.status],
                )}
                data-testid="upcoming-item"
              >
                <div
                  class={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-1",
                    DUE_STATUS_CLASSES[item.status],
                  )}
                >
                  <Show
                    when={item.kind === "warranty"}
                    fallback={
                      item.due?.dueBy === "usage" ? (
                        <Gauge class="h-4 w-4" />
                      ) : (
                        <CalendarClock class="h-4 w-4" />
                      )
                    }
                  >
                    <ShieldCheck class="h-4 w-4" />
                  </Show>
                </div>

                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="truncate text-sm font-medium">{item.title}</span>
                    <span
                      class={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
                        DUE_STATUS_CLASSES[item.status],
                      )}
                      data-testid={`due-status-${item.status}`}
                    >
                      {dueStatusLabels(t)[item.status]}
                    </span>
                  </div>
                  <div class="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Show when={!props.hideAssetName}>
                      <KindIcon class="h-3.5 w-3.5 shrink-0" />
                      <span class="truncate">{item.assetName}</span>
                      <span>·</span>
                    </Show>
                    <span class="truncate" data-testid="upcoming-due-label">
                      {dueLabel(item)}
                    </span>
                  </div>
                </div>

                <ChevronRight class="h-4 w-4 shrink-0 text-muted-foreground/50" />
              </Link>
            </li>
          );
        }}
      </For>
    </ul>
  );
}
